import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getDownloadURL, ref } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { storage } from '../../firebase';
import { millionairesConnectLessonResources } from '../../data/economyPrograms';
import EmbeddedVideoCard from '../../components/domain/EmbeddedVideoCard';
import ResourceRow, { resourceType } from '../../components/domain/ResourceRow';
import { DetailList, EmptyState, InlineAlert, LoadingState, SectionHeading, StatusBadge, Tabs } from '../../components/ui/Primitives';
import { courseName, formatDate, timestampValue } from '../../utils/display';
import { loadMillionairesConnectProgress, persistMillionairesConnectProgress } from '../../utils/courseProgress';
import { isSequentialLessonUnlocked } from '../../utils/lessonSequence';
import { isMillionairesConnectCourse, resourceMatchesActiveCourse } from '../../utils/resourceAccess';
import { hasSeasonSixAccess, isSeasonSixResource } from '../../utils/seasonSixAccess';

const courseTabs = [
  { value: 'overview', label: 'Overview' },
  { value: 'learning', label: 'Learning' },
  { value: 'assignments', label: 'Assignments' },
  { value: 'resources', label: 'Resources' }
];

const assignmentState = (assignment, submissions, userId) => {
  const submission = submissions.find(item => item.assignmentId === assignment.id && item.studentId === userId);
  if (submission?.marks !== null && submission?.marks !== undefined && submission?.marks !== '') return { label: 'Graded', tone: 'success' };
  if (submission) return { label: 'Submitted', tone: 'info' };
  const due = timestampValue(assignment.dueDate && assignment.dueTime ? `${assignment.dueDate}T${assignment.dueTime}` : assignment.dueDate);
  if (due && due < Date.now()) return { label: 'Overdue', tone: 'danger' };
  return { label: 'To do', tone: 'warning' };
};

const resourceGroup = resource => resource.module || resource.group || resource.folder || 'Course materials';

export function CourseExperience({ forcedCourseId = '' }) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    courses = [], assignments = [], submissions = [], resources = [],
    announcements = [], calendarEvents = [], loading = false
  } = useData() || {};
  const courseId = forcedCourseId || params.courseId || user?.courseId || '';
  const [opening, setOpening] = useState('');
  const [error, setError] = useState('');
  const [completedLessonIds, setCompletedLessonIds] = useState(() => new Set());
  const [activeVideo, setActiveVideo] = useState(null);
  const requestedTab = searchParams.get('tab');
  const activeTab = courseTabs.some(tab => tab.value === requestedTab) ? requestedTab : 'overview';
  const course = courses.find(item => item.id === courseId) || null;
  const millionairesConnectEnrolled = user?.courseId === courseId
    && isMillionairesConnectCourse(courses, courseId, user?.intake);
  const millionairesConnectAccess = millionairesConnectEnrolled && hasSeasonSixAccess(user);
  const isEntitled = user?.courseId === courseId
    && (user?.lmsActive === true || user?.accessStatus === 'paid' || millionairesConnectAccess);

  const courseAssignments = useMemo(() => assignments
    .filter(item => (item.courseId && item.courseId === courseId) || (!item.courseId && item.intake && item.intake === user?.intake))
    .sort((a, b) => timestampValue(a.dueDate) - timestampValue(b.dueDate)), [assignments, courseId, user?.intake]);
  const courseResources = useMemo(() => {
    const published = resources.filter(resource => resourceMatchesActiveCourse({
      resource, courses, activeCourseId: courseId, activeIntake: user?.intake
    }));
    return millionairesConnectEnrolled
      ? [...published, ...millionairesConnectLessonResources]
      : published;
  }, [resources, courses, courseId, user?.intake, millionairesConnectEnrolled]);

  useEffect(() => {
    let cancelled = false;
    if (!millionairesConnectEnrolled) {
      setCompletedLessonIds(new Set());
      return undefined;
    }
    loadMillionairesConnectProgress(user?.id).then((ids) => {
      if (!cancelled) setCompletedLessonIds(new Set(ids));
    });
    return () => { cancelled = true; };
  }, [millionairesConnectEnrolled, user?.id]);
  const learningGroups = useMemo(() => {
    const groups = new Map();
    courseResources.forEach(resource => {
      const label = resourceGroup(resource);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(resource);
    });
    return Array.from(groups.entries());
  }, [courseResources]);
  const upcoming = useMemo(() => {
    const assignmentItems = courseAssignments
      .filter(item => timestampValue(item.dueDate) >= Date.now())
      .map(item => ({ id: `assignment-${item.id}`, title: item.title || 'Assignment', date: item.dueDate, type: 'Assignment' }));
    const eventItems = calendarEvents
      .filter(item => (!item.courseId || item.courseId === courseId) && timestampValue(item.date || item.startDate) >= Date.now())
      .map(item => ({ id: `event-${item.id}`, title: item.title || 'Course event', date: item.date || item.startDate, type: 'Event' }));
    return [...assignmentItems, ...eventItems].sort((a, b) => timestampValue(a.date) - timestampValue(b.date)).slice(0, 3);
  }, [courseAssignments, calendarEvents, courseId]);
  const latestAnnouncement = announcements
    .filter(item => item.courseId === courseId || (!item.courseId && item.intake === user?.intake))
    .sort((a, b) => timestampValue(b.createdAt) - timestampValue(a.createdAt))[0];

  const changeTab = tab => setSearchParams(tab === 'overview' ? {} : { tab });
  const isResourceLocked = resource => {
    if (resource.isMillionairesConnectVideo) {
      return !isSequentialLessonUnlocked({
        lessons: millionairesConnectLessonResources,
        lessonIndex: resource.sequenceIndex,
        completedLessonIds,
        hasAccess: millionairesConnectAccess
      });
    }
    return (resource.isPaid && !user?.lmsActive) || (isSeasonSixResource(resource) && !hasSeasonSixAccess(user));
  };
  const openResource = async resource => {
    setError('');
    if (isResourceLocked(resource)) {
      setError('This material is locked. Open Classroom to use the existing access flow.');
      return;
    }
    setOpening(resource.id);
    try {
      const url = resource.embedUrl
        || resource.downloadUrl
        || resource.url
        || (resource.storagePath ? await getDownloadURL(ref(storage, resource.storagePath)) : '');
      if (!url) throw new Error('Resource URL unavailable');
      if (resourceType(resource) === 'Video') {
        setActiveVideo({ resource, sourceUrl: url });
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (resourceError) {
      console.error('Failed to open course resource', resourceError);
      setError('The resource could not be opened. Please try again.');
    } finally {
      setOpening('');
    }
  };

  const completeLesson = resource => {
    const next = new Set(completedLessonIds);
    next.add(resource.lessonId);
    setCompletedLessonIds(next);
    persistMillionairesConnectProgress(user?.id, [...next]);
  };

  if (loading) return <LoadingState label="Loading your course…" />;
  if (!isEntitled || !course) {
    return (
      <EmptyState
        title={course ? 'Course access is not active' : 'Course information is unavailable'}
        description="Your enrolled course will appear here when registration and access are complete."
        action={<Link className="btn btn-primary" to="/student/registration">View registration</Link>}
      />
    );
  }

  const renderRows = (items, emptyCopy) => items.length ? (
    <div className="academic-list">
      {items.map(assignment => {
        const status = assignmentState(assignment, submissions, user?.id);
        return (
          <div className="academic-row" key={assignment.id}>
            <span><strong>{assignment.title || 'Assignment'}</strong><small>{assignment.module || 'Course assignment'}</small></span>
            <time>{formatDate(assignment.dueDate)}</time>
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            <Link className="ui-text-action" to={`/student/assignments?assignment=${assignment.id}`}>View →</Link>
          </div>
        );
      })}
    </div>
  ) : <EmptyState title={emptyCopy} description="New items will appear here when they are published." />;

  return (
    <div className="course-experience">
      <header className="course-page-header">
        <p className="ui-eyebrow">My course</p>
        <h1>{courseName(course)}</h1>
        <p className="course-page-meta">{[course.code, course.faculty, user?.intake].filter(Boolean).join(' · ')}</p>
      </header>
      <Tabs items={courseTabs} active={activeTab} onChange={changeTab} ariaLabel="Course sections" />
      {error && <InlineAlert tone="danger">{error}</InlineAlert>}

      {activeTab === 'overview' && (
        <div className="course-overview-grid">
          <div>
            <section className="course-copy-section">
              <h2>About this course</h2>
              <p>{course.description || course.summary || 'Course information will be added by the academic team.'}</p>
              <button type="button" className="btn btn-primary" onClick={() => changeTab('learning')}>Continue learning</button>
            </section>
            <section className="course-copy-section">
              <SectionHeading title="Upcoming" action={<button type="button" className="ui-text-action" onClick={() => changeTab('assignments')}>View assignments →</button>} />
              {upcoming.length ? <div className="academic-list">{upcoming.map(item => (
                <div className="academic-row" key={item.id}>
                  <span><strong>{item.title}</strong><small>{item.type}</small></span>
                  <time>{formatDate(item.date)}</time><span /><span />
                </div>
              ))}</div> : <p className="ui-muted">No upcoming course dates have been published.</p>}
            </section>
            {latestAnnouncement && <section className="course-copy-section"><p className="ui-eyebrow">Latest announcement</p><h2>{latestAnnouncement.title || 'Course update'}</h2><p>{latestAnnouncement.content || latestAnnouncement.message}</p></section>}
          </div>
          <aside className="course-info-panel">
            <h2>Course information</h2>
            <DetailList items={[
              { label: 'Course code', value: course.code },
              { label: 'Faculty', value: course.faculty },
              { label: 'Intake', value: user?.intake },
              { label: 'Learning access', value: 'Active' }
            ]} />
          </aside>
        </div>
      )}

      {activeTab === 'learning' && (
        <section>
          <SectionHeading title="Learning materials" description="Course material organised by its published module or folder." action={<Link className="btn btn-primary" to="/student/classroom">Open classroom</Link>} />
          {learningGroups.length ? learningGroups.map(([label, items]) => (
            <div className="learning-group" key={label}>
              <p className="learning-group-label">{label}</p>
              {items.map(resource => (
                <React.Fragment key={resource.id}>
                  <ResourceRow resource={resource} busy={opening === resource.id} locked={isResourceLocked(resource)} onOpen={openResource} actionLabel={resourceType(resource) === 'Video' ? (completedLessonIds.has(resource.lessonId) ? 'Rewatch here →' : 'Watch here →') : undefined} />
                  {activeVideo?.resource?.id === resource.id && <EmbeddedVideoCard resource={activeVideo.resource} sourceUrl={activeVideo.sourceUrl} lessons={millionairesConnectLessonResources} completedLessonIds={completedLessonIds} hasAccess={millionairesConnectAccess} onComplete={completeLesson} onSelectLesson={(lesson) => setActiveVideo({ resource: lesson, sourceUrl: lesson.embedUrl })} onClose={() => setActiveVideo(null)} />}
                </React.Fragment>
              ))}
            </div>
          )) : <EmptyState title="No learning material yet" description="Published course material will appear here." />}
        </section>
      )}

      {activeTab === 'assignments' && <section><SectionHeading title="Assignments" description="Coursework published for your active course." />{renderRows(courseAssignments, 'No assignments yet')}</section>}

      {activeTab === 'resources' && (
        <section>
          <SectionHeading title="Resources" description="Reference files and media for this course." />
          {courseResources.length ? courseResources.map(resource => (
            <React.Fragment key={resource.id}>
              <ResourceRow resource={resource} busy={opening === resource.id} locked={isResourceLocked(resource)} onOpen={openResource} actionLabel={resourceType(resource) === 'Video' ? (completedLessonIds.has(resource.lessonId) ? 'Rewatch here →' : 'Watch here →') : undefined} />
              {activeVideo?.resource?.id === resource.id && <EmbeddedVideoCard resource={activeVideo.resource} sourceUrl={activeVideo.sourceUrl} lessons={millionairesConnectLessonResources} completedLessonIds={completedLessonIds} hasAccess={millionairesConnectAccess} onComplete={completeLesson} onSelectLesson={(lesson) => setActiveVideo({ resource: lesson, sourceUrl: lesson.embedUrl })} onClose={() => setActiveVideo(null)} />}
            </React.Fragment>
          )) : <EmptyState title="No resources yet" description="Published resources will appear here." />}
        </section>
      )}
    </div>
  );
}

export default function CourseOverview() {
  return <CourseExperience />;
}
